import { createSelector } from '@reduxjs/toolkit'
import { selectBusinessTravellerApprovalPolicyForUser } from './selectBusinessTravellerApprovalPolicyForUser'
import { selectBusinessTravellerCurrentApprovalRequest } from './selectBusinessTravellerCurrentApprovalRequest'
import { selectFlightBudgetInfo, selectFlightSearchBudgets } from './selectBusinessTravellerFlightBudget'
import { getFlightItems, getFlightItemsView } from 'checkout/selectors/view/flights'
import { getFlightApprovalInvalidReason } from 'businessTraveller/utils/flightApprovalValidation'

/**
 * Selector to determine if we should show the inline flight trip request form
 * instead of the normal payment form.
 *
 * This applies to standalone flight bookings when approval is required
 * and there's no existing approved trip request.
 *
 * Returns true while the approval policy is loading to prevent bypass using refreshing the page.
 * Also returns true while budget data is still being fetched for OUTOFPOLICYONLY scope,
 * to prevent a flash where the payment form briefly appears before budget data arrives.
 */
export const selectShouldShowInlineFlightTripRequestForm = createSelector(
  selectBusinessTravellerApprovalPolicyForUser,
  getFlightItems,
  getFlightItemsView,
  selectBusinessTravellerCurrentApprovalRequest,
  selectFlightBudgetInfo,
  selectFlightSearchBudgets,
  (state: App.State) => state.businessTraveller.businessApprovalPolicy.status,
  (
    approvalPolicy,
    flightItems,
    flightItemsView,
    currentApprovalRequest,
    flightBudgetInfo,
    flightSearchBudgetsResult,
    approvalPolicyStatus,
  ): boolean => {
    // Only handle flights
    if (flightItems.length === 0) {
      return false
    }

    // If the approval policy is still loading, show the trip request form section (loading circle)
    if (approvalPolicyStatus === 'pending') {
      return true
    }

    // If the approved trip request exists (Show payment form if approval is still valid, otherwise show inline form)
    if (currentApprovalRequest?.status === 'APPROVED') {
      const { invalidReason } = getFlightApprovalInvalidReason({
        approvalRequest: currentApprovalRequest,
        approvalPolicy,
        flightItems,
        flightViews: flightItemsView.data,
        flightSearchBudgets: flightSearchBudgetsResult.flightSearchBudgets,
      })
      // Approved and still valid → show payment form
      if (!invalidReason) {
        return false
      }
      // Approved but invalid → require to submit a new trip request → show inline form
      if (approvalPolicy?.scope === 'ALWAYS' || approvalPolicy?.scope === 'OUTOFPOLICYONLY') {
        return true
      }
    }

    // No approval policy means no trip request needed
    if (!approvalPolicy) {
      return false
    }

    // If scope is ALWAYS, trip request is required
    if (approvalPolicy.scope === 'ALWAYS') {
      return true
    }

    // If scope is NEVER, no trip request needed
    if (approvalPolicy.scope === 'NEVER') {
      return false
    }

    // For OUTOFPOLICYONLY, we need flight data to calculate if over budget
    if (!flightItemsView.hasRequiredData) {
      return true
    }

    // For OUTOFPOLICYONLY, show the trip request form with a loading state while budgets are still being fetched
    if (flightSearchBudgetsResult.isBudgetFetchPending) {
      return true
    }

    // For OUTOFPOLICYONLY, show form if over budget or no matching budget exists
    if (flightBudgetInfo?.hasMissingBudget || flightBudgetInfo?.isOutOfBudget) {
      return true
    }

    return false
  },
)
